import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const RconTerminal = ({
  logs = [],
  speed = '0 p/s',
  testedCount = '0 / 8,200',
  portsFound = 0,
  sessionTime = '00:00',
  currentStage = 'IDLE',
  targetHost = 'mc.hypixel.net',
  foundPorts = [],
  selectedPort,
  onSelectPort,
  onClearLogs,
  onCopyLogs,
  onSendCommand,
  isProcessing = false
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [cmdInput, setCmdInput] = useState('');
  const logBodyRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (logBodyRef.current) {
      logBodyRef.current.scrollTop = logBodyRef.current.scrollHeight;
    }
  }, [logs]);

  // Focus input when unlocking ACCESS
  useEffect(() => {
    if (currentStage === 'ACCESS' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentStage]);

  const handleCommandSubmit = useCallback((e) => {
    e.preventDefault();
    if (!cmdInput.trim()) return;
    onSendCommand?.(cmdInput.trim());
    setCmdInput('');
  }, [cmdInput, onSendCommand]);

  const cleanHost = targetHost.split(':')[0].split('.')[0] || 'server';
  const isUnlocked = currentStage === 'ACCESS';

  return (
    <div className="terminal-card">
      {/* Terminal Header */}
      <div className="terminal-header">
        <div className="terminal-left-wrap">
          <div className="terminal-window-dots hidden sm:flex">
            <span className="t-dot red" />
            <span className="t-dot yellow" />
            <span className="t-dot green" />
          </div>
          <div className="terminal-title-text">
            <span className="hidden sm:inline">{isEn ? 'RCON PROCESS LOG' : 'ЖУРНАЛ ПРОЦЕССА RCON'}</span>
            <span className="sm:hidden">{isEn ? 'RCON LOG' : 'КОНСОЛЬ'}</span>
          </div>
        </div>

        <div className="terminal-header-tools">
          <button
            type="button"
            className="t-tool-btn"
            onClick={onClearLogs}
            title={isEn ? 'Clear logs' : 'Очистить консоль'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
            </svg>
            <span>{isEn ? 'Clear' : 'Очистить'}</span>
          </button>
          <button
            type="button"
            className="t-tool-btn"
            onClick={onCopyLogs}
            title={isEn ? 'Copy logs' : 'Копировать текст'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
              <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
            </svg>
            <span className="hidden sm:inline">{isEn ? 'Copy' : 'Копировать'}</span>
            <span className="sm:hidden">{isEn ? 'Copy' : 'Копия'}</span>
          </button>
        </div>
      </div>

      {/* Realtime Metrics Bar */}
      <div className="terminal-metrics-bar">
        <div className="t-metric-item">
          <span className="t-metric-label">
            {isEn ? 'Speed' : 'Скорость'}
          </span>
          <span className="t-metric-value">{speed}</span>
        </div>
        <div className="t-metric-item">
          <span className="t-metric-label">
            {currentStage === 'SCAN'
              ? (isEn ? 'Ports Scanned' : 'Портов проверено')
              : (isEn ? 'Passwords Tested' : 'Паролей проверено')}
          </span>
          <span className="t-metric-value">{testedCount}</span>
        </div>
        <div className="t-metric-item">
          <span className="t-metric-label">
            {isEn ? 'Open Ports' : 'Открытых портов'}
          </span>
          <span className="t-metric-value">{portsFound}</span>
        </div>
        <div className="t-metric-item">
          <span className="t-metric-label">
            {isEn ? 'Session Time' : 'Время сессии'}
          </span>
          <span className="t-metric-value">{sessionTime}</span>
        </div>
      </div>

      {/* Found Open Ports Interactive Panel */}
      {foundPorts && foundPorts.length > 0 && (
        <div className="found-ports-panel">
          <span className="found-ports-title">
            {isEn ? 'Open Ports:' : 'Открытые порты:'}
          </span>
          {foundPorts.map((p, i) => {
            const portNum = typeof p === 'object' ? p.port : p;
            const isSel = selectedPort === portNum;
            return (
              <button
                key={i}
                type="button"
                className={`found-port-chip ${isSel ? 'selected' : ''}`}
                onClick={() => onSelectPort?.(portNum)}
                title={isEn ? `Crack port ${portNum}` : `Подбирать пароль к порту ${portNum}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span>{portNum}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Terminal Body with user-select enabled */}
      <div className="terminal-body" ref={logBodyRef}>
        {logs.map((log, index) => {
          const tagClass = log.tagClass || 'info';
          const tagText = log.tagText || '[SYSTEM]';

          return (
            <div key={index} className="log-line">
              <span className="log-time">{log.time}</span>{' '}
              <span className={`log-tag ${tagClass}`}>{tagText}</span>{' '}
              <span className={`log-text ${log.isHighlight ? 'highlight' : ''}`}>
                {log.text}
              </span>
            </div>
          );
        })}
      </div>

      {/* Interactive Shell Command Box */}
      <div className="interactive-shell-box">
        {!isUnlocked ? (
          <div className="shell-lock-overlay">
            <span className="shell-lock-title">
              🔒 {isEn
                ? 'Interactive RCON console will be unlocked at stage '
                : 'Интерактивная RCON-консоль будет разблокирована на стадии '}
              <strong className="shell-lock-stage">ACCESS</strong>
            </span>
            <span className="shell-lock-hint">
              {isEn ? '(Or use "Direct Connection")' : '(Либо используйте «Прямое подключение»)'}
            </span>
          </div>
        ) : (
          <form className="shell-input-form" onSubmit={handleCommandSubmit}>
            <span className="shell-prompt">rcon@{cleanHost}:~$</span>
            <input
              ref={inputRef}
              type="text"
              className="shell-input"
              value={cmdInput}
              onChange={(e) => setCmdInput(e.target.value)}
              placeholder={isEn ? 'Enter command (/help, /list, /say, /op, /whitelist)...' : 'Введите команду (/help, /list, /say, /op, /whitelist)...'}
              autoComplete="off"
            />
            <button
              type="submit"
              className="shell-send-btn !text-black"
              style={{ color: '#000000' }}
            >
              <span className="!text-black" style={{ color: '#000000' }}>
                {isEn ? 'Execute' : 'Выполнить'}
              </span>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#000000"
                strokeWidth="2.2"
                style={{ stroke: '#000000' }}
                className="!stroke-black"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default RconTerminal;
